import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type YesterdayOrders } from '../api/client'
import { fmtInt, fmtMoney } from '../lib/format'

export function YesterdayOrdersPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['orders-yesterday'],
    queryFn: () => api.get<YesterdayOrders>('/api/orders/yesterday'),
    refetchInterval: 60_000,
  })
  const [open, setOpen] = useState(false)

  return (
    <section className="border border-ink-500 bg-ink-800/40">
      <div className="label px-4 pt-3 pb-2 text-paper-faint">Yesterday's Orders</div>

      {isLoading && <div className="label text-paper-trace px-4 pb-4">Loading…</div>}

      {error && <div className="label text-radar-red px-4 pb-4">Failed to load orders.</div>}

      {data && (
        <>
          <div className="px-4 pb-3">
            <div className="font-mono text-[15px] text-paper tabular-nums">
              <span className="text-paper-trace">{data.window.label} · </span>
              <span className="text-paper font-bold">{fmtInt(data.order_count)} orders</span>
              <span className="text-paper-trace"> — </span>
              <span className="text-radar-green">{fmtInt(data.new)} new</span>
              <span className="text-paper-trace"> · </span>
              <span className="text-radar-cyan">{fmtInt(data.renewal)} renewal</span>
              {data.unknown > 0 && (
                <>
                  <span className="text-paper-trace"> · </span>
                  <span className="text-paper-dim">{fmtInt(data.unknown)} unknown</span>
                </>
              )}
              <span className="text-paper-trace"> · </span>
              <span className="text-amber-glow">{fmtMoney(data.gross_collected, false, 2)} collected</span>
            </div>
            {data.as_of && (
              <div className="font-mono text-[10px] text-paper-trace mt-1">
                as of {new Date(data.as_of).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET
              </div>
            )}
            {(data.refund_count > 0 || data.void_count > 0) && (
              <div className="font-mono text-[11px] text-radar-red/80 mt-1">
                {data.refund_count > 0 && <>· {fmtInt(data.refund_count)} refund −{fmtMoney(data.refund_amount, false, 2)} </>}
                {data.void_count > 0 && <>· {fmtInt(data.void_count)} void</>}
              </div>
            )}
          </div>

          {data.multi_line_note && (
            <div className="font-mono text-[10px] text-paper-trace px-4 pb-2">
              Includes multi-subscription bundles; line counts exceed order count.
            </div>
          )}

          {/* Per-publication breakdown */}
          {data.by_publication.length > 0 && (
            <div className="px-4 pb-3 border-t border-ink-500/30 pt-2">
              <table className="w-full text-[12px] font-mono">
                <tbody>
                  {data.by_publication.map((p) => (
                    <tr key={p.publication_slug} className="text-paper-dim">
                      <td className="py-0.5">{p.publication_name}</td>
                      <td className="py-0.5 text-right tabular-nums text-paper">{fmtInt(p.count)}</td>
                      <td className="py-0.5 text-right tabular-nums text-amber-glow">{fmtMoney(p.gross, false, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Dedicated drill-down toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="w-full text-left px-4 py-2 border-t border-ink-500/30 font-mono text-[12px] text-paper-trace hover:bg-ink-700/30 hover:text-paper-dim"
          >
            {open ? '▾ Hide individual orders' : `▸ ${fmtInt(data.order_count)} individual orders`}
          </button>

          {/* Inline drill-down */}
          {open && (
            <div className="px-4 pb-4 border-t border-ink-500/30 pt-2 overflow-x-auto">
              {data.orders.length === 0 ? (
                <div className="label text-paper-trace">No orders.</div>
              ) : (
                <table className="w-full text-[12px] font-mono">
                  <thead>
                    <tr className="text-paper-trace">
                      <th className="label py-1 font-normal text-left">Time</th>
                      <th className="label py-1 font-normal text-left">Payer</th>
                      <th className="label py-1 font-normal text-left">Title</th>
                      <th className="label py-1 font-normal text-left">Type</th>
                      <th className="label py-1 font-normal text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((o) => (
                      <tr key={o.simplecirc_order_id} className="border-t border-ink-500/20 text-paper-dim">
                        <td className="py-1">{o.order_date ? new Date(o.order_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) : '—'}</td>
                        <td className="py-1 text-paper">{o.payer_full_name ?? '—'}</td>
                        <td className="py-1">{o.publication_name ?? '—'}</td>
                        <td className="py-1">{o.new_or_renewal}</td>
                        <td className="py-1 text-right tabular-nums text-amber-glow">{fmtMoney(o.amount_paid, false, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}
